import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Badge } from "../../ui/badge";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { VoiceHint } from "../VoiceHint";
import { Mail, CheckCircle, AlertCircle, Clock, FileText, DollarSign } from "lucide-react";
import { trackEvent } from "../../../config/analytics";

interface NotificationsProfile {
  emailVerified: boolean;
  weeklyEmail: boolean;
  emailDay: string;
  emailTime: string;
  pdfFormat: string;
  budgetTips: boolean;
}

interface NotificationsEmailSectionProps {
  profile: NotificationsProfile;
  onUpdate: (updates: Partial<NotificationsProfile>) => void;
  currentTier: string;
}

export function NotificationsEmailSection({ 
  profile, 
  onUpdate, 
  currentTier 
}: NotificationsEmailSectionProps) {
  
  const handleResendVerification = () => {
    console.log("Resend verification email");
    // This would trigger the resendVerificationEmail action
  };

  const weekdays = [
    { value: "sunday", label: "Sunday" },
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" }
  ];

  const timeOptions = [
    "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
    "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
    "18:00", "19:00", "20:00", "21:00", "22:00"
  ];

  const isPro = currentTier === "pro";
  const isCore = currentTier === "core" || currentTier === "pro";

  return (
    <div className="space-y-8">
      <VoiceHint 
        message="Last but not least! Let's set up your email preferences so I can send you perfectly timed meal plans and helpful tips. 📧"
        voice="medium"
      />

      {/* Email Verification Status */}
      <Card className={profile.emailVerified ? "border-green-200 bg-green-50" : "border-coral-200 bg-coral-50"}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {profile.emailVerified ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-warning-600" />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {profile.emailVerified ? "Email Verified" : "Email Not Verified"}
                  </span>
                  <Badge className={
                    profile.emailVerified 
                      ? "bg-green-100 text-green-800 border-green-200"
                      : "bg-coral-100 text-coral-800 border-coral-200"
                  }>
                    {profile.emailVerified ? "Verified" : "Pending"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {profile.emailVerified 
                    ? "You'll receive meal plan emails and updates"
                    : "Verify your email to receive meal plans and updates"
                  }
                </p>
              </div>
            </div>
            
            {!profile.emailVerified && (
              <Button
                onClick={handleResendVerification}
                size="sm"
                className="bg-coral-600 desktop-hover:bg-coral-700"
              >
                Resend Verification
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Weekly Plan Email */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Weekly Plan Email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Send weekly meal plan</Label>
              <p className="text-xs text-muted-foreground">
                Get your personalized meal plan delivered to your inbox
              </p>
            </div>
            <Switch
              checked={profile.weeklyEmail}
              onCheckedChange={(checked) => {
                onUpdate({ weeklyEmail: checked })
                try { trackEvent('weekly_digest_toggled', { enabled: checked }) } catch {}
              }}
            />
          </div>

          {/* Email Timing */}
          {profile.weeklyEmail && (
            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Day Selection */}
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Day of the week
                  </Label>
                  <Select 
                    value={profile.emailDay} 
                    onValueChange={(value) => onUpdate({ emailDay: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      {weekdays.map((day) => (
                        <SelectItem key={day.value} value={day.value}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Time Selection */}
                <div className="space-y-2">
                  <Label className="text-sm">Time</Label>
                  <Select 
                    value={profile.emailTime} 
                    onValueChange={(value) => onUpdate({ emailTime: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeOptions.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Your meal plan will arrive every {weekdays.find(d => d.value === profile.emailDay)?.label} at {profile.emailTime}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PDF Format */}
      <Card className={!isCore ? "opacity-60" : ""}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              PDF Format
            </CardTitle>
            {!isCore && (
              <Badge className="bg-warning/10 text-warning border-warning/20">
                Family+
              </Badge>
            )}
          </div>
          {isCore && (
            <p className="text-sm text-muted-foreground">
              Choose your preferred PDF layout
            </p>
          )}
        </CardHeader>
        <CardContent>
          {isCore ? (
            <RadioGroup
              value={profile.pdfFormat}
              onValueChange={(value) => {
                onUpdate({ pdfFormat: value })
                try { trackEvent('pdf_format_changed', { format: value }) } catch {}
                try { localStorage.setItem('nura.pdfFormat', String(value)) } catch {}
              }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="flex items-center space-x-2 border rounded-lg p-4">
                <RadioGroupItem value="compact" id="pdf-compact" />
                <div className="flex-1">
                  <Label htmlFor="pdf-compact" className="text-sm font-medium">
                    Compact
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Essential info, minimal pages
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 border rounded-lg p-4">
                <RadioGroupItem value="detailed" id="pdf-detailed" />
                <div className="flex-1">
                  <Label htmlFor="pdf-detailed" className="text-sm font-medium">
                    Detailed
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Full recipes, tips, and notes
                  </p>
                </div>
              </div>
            </RadioGroup>
          ) : (
            <div className="p-4 border rounded-lg bg-muted/30">
              <p className="text-sm text-muted-foreground text-center">
                Upgrade to Family to choose your PDF format and get detailed meal plan downloads.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Budget Tips (Pro Only) */}
      <Card className={!isPro ? "opacity-60" : ""}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Budget Mode Tips
            </CardTitle>
            {!isPro && (
              <Badge className="bg-sage/10 text-sage border-sage/20">
                Master Chef
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isPro ? (
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Send me budget optimization tips</Label>
                <p className="text-xs text-muted-foreground">
                  Weekly emails with cost-saving strategies and ingredient substitutions
                </p>
              </div>
              <Switch
                checked={profile.budgetTips}
                onCheckedChange={(checked) => onUpdate({ budgetTips: checked })}
              />
            </div>
          ) : (
            <div className="p-4 border rounded-lg bg-muted/30">
              <p className="text-sm text-muted-foreground text-center">
                Master Chef users get exclusive budget optimization tips and cost-saving strategies.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Preview */}
      {profile.weeklyEmail && profile.emailVerified && (
        <VoiceHint 
          message="Perfect! You're all set to receive your weekly meal plans. I'll make sure they arrive right on time with everything you need for a successful week of cooking! ✨"
          voice="high"
        />
      )}
    </div>
  );
}
